import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} ConfirmRazorpayPaymentData
 * @property {number} invoice_id - Invoice ID
 * @property {string} razorpay_payment_id - Razorpay payment ID from callback
 * @property {string} razorpay_order_id - Razorpay order ID from callback
 * @property {string|number} amount - Payment amount
 * @property {string} currency - Currency code (e.g., "INR")
 */

/**
 * @typedef {Object} ConfirmPaymentResponse
 * @property {boolean} success - Whether the confirmation was successful
 * @property {string} message - Response message
 * @property {any} meta - Additional metadata
 */

/**
 * @description Custom hook to confirm Razorpay payment after successful transaction
 */
export const useConfirmRazorPayPayment = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const { axiosInstance } = useAxios();

    /**
     * @description Confirm Razorpay payment
     * @async
     * @param {ConfirmRazorpayPaymentData} paymentData - The payment confirmation data
     * @param {() => void} [onSuccess] - Callback function to be called on success
     * @returns {Promise<ConfirmPaymentResponse | null>} Returns response on success, null on failure
     */
    const confirmPayment = async (paymentData, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);

            const response = await axiosInstance.post(
                `${TENANT_DOMAIN}/billing/payments/online/razorpay/confirm`,
                {
                    invoice_id: paymentData.invoice_id,
                    razorpay_payment_id: paymentData.razorpay_payment_id,
                    razorpay_order_id: paymentData.razorpay_order_id,
                    amount: String(paymentData.amount),
                    currency: paymentData.currency || "INR",
                }
            );

            const data = response.data;
            setSuccess(true);

            if (onSuccess) onSuccess(data);

            return data;
        } catch (err) {
            setSuccess(false);

            if (err?.response?.status === 500 || err?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else if (err?.response?.data?.error_code === "VALIDATION_FAILED") {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];
                setError(firstError || err?.response?.data?.message || "Validation failed");
            } else {
                const apiErrors = err?.response?.data?.errors;
                const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

                setError(
                    firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN
                );
            }

            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the hook state
     */
    const reset = () => {
        setLoading(false);
        setError("");
        setSuccess(false);
    };

    return {
        loading,
        error,
        success,
        confirmPayment,
        reset,
    };
};

export default useConfirmRazorPayPayment;